import React, { useState } from 'react';
import { XIcon, AlertCircleIcon } from 'lucide-react';
interface GatedContentFormProps {
  isOpen: boolean;
  onClose: () => void;
  playTitle?: string;
  onSubmit?: (data: any) => void;
}
export function GatedContentForm({
  isOpen,
  onClose,
  playTitle = 'GTM Play',
  onSubmit
}: GatedContentFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    company: '',
    role: '',
    gtmSize: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const roles = ['Marketing', 'Sales', 'RevOps', 'Customer Success', 'Partner'];
  const gtmSizes = ['1-10', '11-50', '51-200', '200+'];
  const validateEmail = (email: string) => {
    const workDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    const domain = email.split('@')[1];
    if (workDomains.includes(domain)) {
      return 'Please use your work email address';
    }
    return null;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    if (!formData.company) newErrors.company = 'Company is required';
    if (!formData.role) newErrors.role = 'Please select your role';
    if (!formData.gtmSize) newErrors.gtmSize = 'Please select your GTM size';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsLoading(true);
    if (onSubmit) {
      onSubmit(formData);
    }
    setTimeout(() => {
      setIsLoading(false);
      onClose();
    }, 1500);
  };
  const handleChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6 animate-in fade-in duration-200" data-id="element-433">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in slide-in-from-bottom duration-300" data-id="element-434">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-435">
          <div data-id="element-436">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-437">
              Get this GTM play
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-438">{playTitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-439">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-440" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-441">
          <p className="text-gray-700 mb-6" data-id="element-442">
            Tell us who you are and we'll show you how to run this in GTM Studio
            and Copilot.
          </p>
          {/* Alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-id="element-443">
            <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-444" />
            <p className="text-sm text-blue-900" data-id="element-445">
              This play uses AI email and buying group detection. Some features
              require Copilot.
            </p>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-446">
            <div data-id="element-447">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-448">
                Work Email *
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="name@work.com" className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} data-id="element-449" />
              {errors.email && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-450">
                  <span data-id="element-451">⚠</span>
                  {errors.email}
                </p>}
            </div>
            <div data-id="element-452">
              <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-453">
                Company *
              </label>
              <input type="text" id="company" value={formData.company} onChange={e => handleChange('company', e.target.value)} placeholder="Your company name" className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.company ? 'border-red-500' : 'border-gray-300'}`} data-id="element-454" />
              {errors.company && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-455">
                  <span data-id="element-456">⚠</span>
                  {errors.company}
                </p>}
            </div>
            <div data-id="element-457">
              <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-458">
                Role *
              </label>
              <select id="role" value={formData.role} onChange={e => handleChange('role', e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.role ? 'border-red-500' : 'border-gray-300'}`} data-id="element-459">
                <option value="" data-id="element-460">Select your role</option>
                {roles.map(role => <option key={role} value={role} data-id="element-461">
                    {role}
                  </option>)}
              </select>
              {errors.role && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-462">
                  <span data-id="element-463">⚠</span>
                  {errors.role}
                </p>}
            </div>
            <div data-id="element-464">
              <label htmlFor="gtmSize" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-465">
                GTM Team Size *
              </label>
              <select id="gtmSize" value={formData.gtmSize} onChange={e => handleChange('gtmSize', e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.gtmSize ? 'border-red-500' : 'border-gray-300'}`} data-id="element-466">
                <option value="" data-id="element-467">Select team size</option>
                {gtmSizes.map(size => <option key={size} value={size} data-id="element-468">
                    {size}
                  </option>)}
              </select>
              {errors.gtmSize && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-469">
                  <span data-id="element-470">⚠</span>
                  {errors.gtmSize}
                </p>}
            </div>
            <div className="flex gap-3 pt-4" data-id="element-471">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 font-semibold transition-all duration-200" data-id="element-472">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-473">
                {isLoading ? 'Loading...' : 'Show me the play'}
              </button>
            </div>
          </form>
          {/* Secondary CTA */}
          <div className="mt-4 text-center" data-id="element-474">
            <button className="text-sm text-gray-600 hover:text-gray-900 font-medium" data-id="element-475">
              Book a GTM demo instead →
            </button>
          </div>
        </div>
      </div>
    </div>;
}